/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from 'react';

import { AlertResponse } from '@/client';
import { PaginatedTable } from '@/components/PaginatedTable';
import { QueryWrapper } from '@/components/QueryWrapper';
import { useMessage } from '@/hooks/message';
import { useTestAlertMutation } from '@/hooks/mutation/useTestAlertMutation';
import { useAlertResponsesQuery } from '@/hooks/query/useAlertResponsesQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Select } from '@/primitives/Select';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { AlertResponseExpandedRow } from './AlertResponseExpandedRow';

dayjs.extend(utc);

const REFRESH_INTERVAL_MS = 10000; // 10 seconds
const POLLING_TIMEOUT_MS = 60000; // 1 minute

function renderExpandedRow(record: AlertResponse) {
  return <AlertResponseExpandedRow response={record} />;
}

interface AlertConfigDetailsResponsesProps {
  alertId: string;
}

export function AlertConfigDetailsResponses({ alertId }: AlertConfigDetailsResponsesProps) {
  const { t } = useMimicTranslation('notificationManagement');
  const [messageApi] = useMessage();
  const queryClient = useQueryClient();

  const [selectedEventType, setSelectedEventType] = useState<'hallmark' | 'deflection' | 'signal' | 'lifecycle'>(
    'hallmark',
  );
  const [waitingForTestId, setWaitingForTestId] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const alertResponsesQuery = useAlertResponsesQuery({ alertId, type: 'inMemory' });

  const { mutate: sendTestAlert, isPending: isSendingTest } = useTestAlertMutation({
    onSuccess: (data) => {
      const { testId } = data.data;
      setWaitingForTestId(testId);
      messageApi.success(t('responses.testAlertQueued'));
      queryClient.invalidateQueries({ queryKey: ['alertResponses'] });
    },
    onError: () => {
      messageApi.error(t('responses.testAlertError'));
    },
  });

  // Auto-refresh effect - only when waiting for test result
  useEffect(() => {
    if (!waitingForTestId || !alertId || !selectedTenantId) return undefined;

    // Set up polling interval
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['alertResponses'] });
    }, REFRESH_INTERVAL_MS);

    intervalRef.current = interval;

    // Set up timeout to stop polling after 1 minute
    const timeout = setTimeout(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setWaitingForTestId(null);
      messageApi.warning(t('responses.testAlertTimeout'));
    }, POLLING_TIMEOUT_MS);

    timeoutRef.current = timeout;

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [waitingForTestId, alertId, selectedTenantId, queryClient, messageApi, t]);

  // Check if test response arrived
  useEffect(() => {
    if (!waitingForTestId || !alertResponsesQuery.data?.data) return;

    const foundResponse = alertResponsesQuery.data.data.find((response) => response.telemetryId === waitingForTestId);

    if (foundResponse) {
      // Stop auto-refresh and timeout
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setWaitingForTestId(null);
      messageApi.success(t('responses.testAlertReceived'));
    }
  }, [alertResponsesQuery.data, waitingForTestId, messageApi, t]);

  const handleTestAlert = () => {
    if (!alertId) return;

    sendTestAlert({
      id: alertId,
      requestBody: { eventType: selectedEventType },
    });
  };

  const columns: ColumnsType<AlertResponse> = [
    {
      title: t('responses.eventType'),
      dataIndex: 'telemetryType',
      key: 'telemetryType',
      render: (type: string, record: AlertResponse) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text>{type}</Text>
          {record.isTest && <Tag color="blue">{t('responses.testBadge')}</Tag>}
        </div>
      ),
    },
    {
      title: t('responses.status'),
      dataIndex: 'statusCode',
      key: 'statusCode',
      render: (statusCode: number | null, record: AlertResponse) => {
        const isSuccess = !record.error && statusCode && statusCode < 400;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isSuccess ? (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
            )}
            <Text>{statusCode || 'N/A'}</Text>
          </div>
        );
      },
    },
    {
      title: t('responses.timestamp'),
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      sorter: true,
      render: (date: string) => <Text size="sm">{dayjs(date).utc().format('MMM DD, YYYY – HH:mm:ss')} (UTC)</Text>,
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
        <Select
          value={selectedEventType}
          onChange={setSelectedEventType}
          options={[
            { label: t('parameters.events.hallmark'), value: 'hallmark' },
            { label: t('parameters.events.deflection'), value: 'deflection' },
            { label: t('parameters.events.signal'), value: 'signal' },
            { label: t('parameters.events.lifecycle'), value: 'lifecycle' },
          ]}
          style={{ width: '160px' }}
          disabled={isSendingTest}
        />
        <Button
          type="primary"
          onClick={handleTestAlert}
          loading={isSendingTest || !!waitingForTestId}
          disabled={isSendingTest || !!waitingForTestId}
        >
          {waitingForTestId ? t('responses.waitingForResponse') : t('responses.generateTest')}
        </Button>
      </div>

      <QueryWrapper queryResult={alertResponsesQuery}>
        {(data) => (
          <PaginatedTable
            data={data}
            columns={columns}
            rowKey="id"
            onParamsChange={alertResponsesQuery.updateQueryParams}
            expandable={{
              expandedRowRender: renderExpandedRow,
              rowExpandable: () => true,
            }}
            hidePaginationInfo={false}
          />
        )}
      </QueryWrapper>
    </div>
  );
}
